import {IconButton, Tooltip} from 'platform/components';
import {Box, Icon, Show, Center} from 'platform/foundation';

import {UserSidebarIconSize} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {useNotifications} from '@dms/notifications';
import {useFocusControl} from '@dms/shared';

import {isDefinedNonZeroValue, suffixTestId, TestIdProps} from 'shared/web';

import {SIDEBAR_MOBILE_ICON_SIZE} from '../constants/sidebarMobileIconSize';
import {useIsMobileNavigation} from '../hooks/useIsMobileNavigation';
import {HeaderButtonWrapper} from './HeaderButtonWrapper';
import {NavigationButton} from './NavigationButton';

const NOTIFICATION_PAGE_SIZE = 20;

interface NotificationsButtonProps extends TestIdProps {
  isOpen: boolean;
  iconSize: UserSidebarIconSize;
}

function NotificationsButtonMobile(props: NotificationsButtonProps) {
  const {unreadNotificationsCount} = useNotifications(NOTIFICATION_PAGE_SIZE);
  const {isDisabled} = useFocusControl();

  return (
    <NavigationButton
      isActive={props.isOpen}
      badgeValue={
        isDefinedNonZeroValue(unreadNotificationsCount) ? unreadNotificationsCount : undefined
      }
      tabIndex={isDisabled ? -1 : 0}
    >
      <Icon size={SIDEBAR_MOBILE_ICON_SIZE} value="social/notifications" color="text.white" />
    </NavigationButton>
  );
}

function NotificationsButtonDesktop(props: NotificationsButtonProps) {
  const {hasUnreadNotifications} = useNotifications(NOTIFICATION_PAGE_SIZE);
  const {isDisabled} = useFocusControl();

  return (
    <Box position="relative" width="100%" height="100%">
      <Center width="100%" height="100%">
        <HeaderButtonWrapper $isOpen={props.isOpen}>
          <Tooltip label={i18n.t('general.notifications.title')} placement="bottom">
            <IconButton
              aria-label="topBar"
              iconSize={props.iconSize}
              icon="social/notifications"
              tabIndex={isDisabled ? -1 : 0}
              data-testid={suffixTestId('notifications', props)}
            />
          </Tooltip>

          <Show when={hasUnreadNotifications}>
            <Box
              position="absolute"
              top={1}
              right={1}
              backgroundColor="severity.danger"
              width={2}
              height={2}
              borderRadius="circular"
            />
          </Show>
        </HeaderButtonWrapper>
      </Center>
    </Box>
  );
}

export function NotificationsButton(props: NotificationsButtonProps) {
  const isMobileNavigation = useIsMobileNavigation();

  if (isMobileNavigation) {
    return <NotificationsButtonMobile {...props} />;
  }

  return <NotificationsButtonDesktop {...props} />;
}
